import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { Calendar, MapPin, Clock, Users, GraduationCap, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { useContent } from "@/lib/siteContent";
import {
  pickFeaturedOpenHouse, todayInTimezone, formatOpenHouseTime,
  PUBLIC_OPEN_HOUSE_STATUSES, DEFAULT_OPEN_HOUSE_CONTACT_EMAIL,
} from "@/lib/openHouseSchedule";

type EventRow = {
  id: string;
  title: string;
  event_date: string;
  start_time: string;
  end_time: string | null;
  end_time_approximate: boolean;
  location_name: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  what_to_expect: string | null;
  status: string;
  status_note: string | null;
  contact_email: string | null;
  capacity_total: number | null;
  public_visible: boolean;
  children_welcome: boolean;
};

type ClassOption = { id: string; title: string };

type Role = null | "parent" | "teacher";

function formatDate(d: string) {
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}

export default function OpenHouse() {
  const [event, setEvent] = useState<EventRow | null>(null);
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<Role>(null);
  const [submitted, setSubmitted] = useState<{ headcount: number } | null>(null);

  useEffect(() => {
    (async () => {
      const today = todayInTimezone();
      const { data: events } = await (supabase as any)
        .from("open_house_events")
        // Explicit public field list — internal fields (created_by, reminder
        // copy/schedule, timestamps) are intentionally not fetched.
        .select(
          "id, title, event_date, start_time, end_time, end_time_approximate, location_name, address_line_1, address_line_2, city, state, postal_code, what_to_expect, children_welcome, status, status_note, contact_email, capacity_total, public_visible"
        )
        .eq("public_visible", true)
        .in("status", [...PUBLIC_OPEN_HOUSE_STATUSES])
        .gte("event_date", today)
        .order("event_date", { ascending: true })
        .limit(10);
      const evt = pickFeaturedOpenHouse((events ?? []) as EventRow[], today);
      setEvent(evt);

      // Class list is filtered by class_registry (single source of truth for
      // public visibility). Legacy `classes.hidden` is no longer consulted.
      const [{ data: cls }, { data: registry }] = await Promise.all([
        (supabase as any)
          .from("classes")
          .select("id, slug, title")
          .in("status", ["published", "coming_soon"])
          .order("title", { ascending: true }),
        (supabase as any)
          .from("class_registry")
          .select("slug, linked_class_id, visibility_status"),
      ]);
      const published = new Set<string>();
      for (const r of (registry ?? []) as Array<{ slug: string; linked_class_id: string | null; visibility_status: string }>) {
        if (r.visibility_status === "published") {
          if (r.slug) published.add(r.slug);
          if (r.linked_class_id) published.add(r.linked_class_id);
        }
      }
      const visible = ((cls ?? []) as any[]).filter((c) => {
        // Unknown → allow (matches useClassRegistry default). Known → must be published.
        const hasRegistryRow = (registry ?? []).some((r: any) => r.slug === c.slug || r.linked_class_id === c.id);
        return !hasRegistryRow || published.has(c.slug) || published.has(c.id);
      });
      setClasses(visible.map((c) => ({ id: c.id, title: c.title })));

      setLoading(false);
    })();
  }, []);

  const interrupted = event?.status === "postponed" || event?.status === "cancelled";
  const registrationOpen = event?.status === "registration_open";

  return (
    <div className="public-site pt-14 min-h-screen bg-background">
      <div className="container mx-auto max-w-3xl px-4 py-10 space-y-8">
        {loading ? (
          <div className="py-24 text-center text-muted-foreground">
            <Loader2 className="mx-auto h-6 w-6 animate-spin" />
          </div>
        ) : !event ? (
          <NoEvent />
        ) : submitted ? (
          <SubmittedState headcount={submitted.headcount} event={event} onReset={() => { setSubmitted(null); setRole(null); }} />
        ) : (
          <>
            {interrupted && <StatusNotice event={event} />}
            <EventHero event={event} interrupted={interrupted} />
            {interrupted ? null : !registrationOpen ? (
              <ClosedNotice event={event} />
            ) : role === null ? (
              <RolePicker onPick={setRole} childrenWelcome={event.children_welcome} />
            ) : role === "parent" ? (
              <ParentForm
                event={event}
                classes={classes}
                onBack={() => setRole(null)}
                onSubmitted={(h) => setSubmitted({ headcount: h })}
              />
            ) : (
              <TeacherForm
                event={event}
                classes={classes}
                onBack={() => setRole(null)}
                onSubmitted={(h) => setSubmitted({ headcount: h })}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ContactLine({ email }: { email: string }) {
  return (
    <p className="text-sm text-muted-foreground">
      Questions? Email us at{" "}
      <a className="underline" href={`mailto:${email}`}>{email}</a>.
    </p>
  );
}

function NoEvent() {
  const title = useContent("open_house.no_event.title", "Open House");
  return (
    <Card>
      <CardContent className="py-16 text-center space-y-3">
        <h1 className="font-heading text-3xl">{title}</h1>
        <p className="text-muted-foreground">Our next Open House date will be announced soon.</p>
        <ContactLine email={DEFAULT_OPEN_HOUSE_CONTACT_EMAIL} />
      </CardContent>
    </Card>
  );
}

function StatusNotice({ event }: { event: EventRow }) {
  const cancelled = event.status === "cancelled";
  const fallback = cancelled
    ? `${event.title} on ${formatDate(event.event_date)} has been cancelled. We're so sorry for the inconvenience.`
    : `${event.title} on ${formatDate(event.event_date)} will not take place as scheduled. We'll post a new date here as soon as it's confirmed.`;
  return (
    <Card className="border-amber-500/40 bg-amber-500/10">
      <CardContent className="py-6 space-y-2 text-center">
        <p className="text-xs uppercase tracking-wide font-semibold text-amber-700">Important update</p>
        <h2 className="font-heading text-2xl">
          {cancelled ? "This Open House has been cancelled" : "This Open House has been postponed"}
        </h2>
        <p className="text-muted-foreground whitespace-pre-line">{event.status_note?.trim() || fallback}</p>
        <ContactLine email={event.contact_email || DEFAULT_OPEN_HOUSE_CONTACT_EMAIL} />
      </CardContent>
    </Card>
  );
}

function EventHero({ event, interrupted = false }: { event: EventRow; interrupted?: boolean }) {
  const addr = [event.address_line_1, event.address_line_2].filter(Boolean).join(", ");
  const cityLine = [event.city, event.state].filter(Boolean).join(", ") +
    (event.postal_code ? ` ${event.postal_code}` : "");
  const timeStr = formatOpenHouseTime(event.start_time, event.end_time, event.end_time_approximate);
  const statusWord = event.status === "cancelled" ? "Cancelled" : "Postponed";
  const eyebrow = useContent("open_house.hero.eyebrow", "You're invited");
  const subtitle = useContent(
    "open_house.hero.subtitle",
    "Come meet our instructors, walk through this year's classes, and ask every question you have.",
  );
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-sm uppercase tracking-wide text-primary font-semibold">
          {interrupted ? statusWord : eyebrow}
        </p>
        <h1 className="font-heading text-4xl md:text-5xl">{event.title}</h1>
        {!interrupted && <p className="text-lg text-muted-foreground">{subtitle}</p>}
      </div>
      <Card>
        <CardContent className="py-5 space-y-3 text-sm">
          <div className="flex items-start gap-3">
            <Calendar className="h-4 w-4 mt-0.5 text-primary" />
            <div>
              <strong className={interrupted ? "line-through text-muted-foreground" : undefined}>
                {formatDate(event.event_date)}
              </strong>
              {interrupted && <span className="ml-2 font-semibold text-amber-700">{statusWord}</span>}
            </div>
          </div>

          {timeStr && (
            <div className="flex items-start gap-3">
              <Clock className="h-4 w-4 mt-0.5 text-primary" />
              <div>{timeStr}</div>
            </div>
          )}
          {(event.location_name || addr) && (
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 mt-0.5 text-primary" />
              <div>
                {event.location_name && <div><strong>{event.location_name}</strong></div>}
                {addr && <div>{addr}</div>}
                {cityLine.trim() && <div>{cityLine}</div>}
              </div>
            </div>
          )}
          {event.capacity_total != null && !interrupted && (
            <div className="flex items-start gap-3">
              <Users className="h-4 w-4 mt-0.5 text-primary" />
              <div>Space for {event.capacity_total} guests</div>
            </div>
          )}
          {event.what_to_expect && (
            <p className="text-muted-foreground pt-2 border-t whitespace-pre-line">{event.what_to_expect}</p>
          )}
          {!interrupted && (
            <div className="pt-2 border-t">
              <ContactLine email={event.contact_email || DEFAULT_OPEN_HOUSE_CONTACT_EMAIL} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ClosedNotice({ event }: { event: EventRow }) {
  const title = useContent("open_house.closed.title", "RSVPs are closed");
  const body = useContent(
    "open_house.closed.body",
    "RSVP for {{event_title}} isn't open right now. You're still welcome to walk in on the day of.",
    { event_title: event.title },
  );
  return (
    <Card>
      <CardContent className="py-10 text-center space-y-2">
        <h2 className="font-heading text-2xl">{title}</h2>
        <p className="text-muted-foreground">{body}</p>
      </CardContent>
    </Card>
  );
}

function RolePicker({ onPick, childrenWelcome }: { onPick: (r: Role) => void; childrenWelcome: boolean }) {
  const parentTitle = useContent("open_house.role_parent.title", "I'm a parent");
  const parentDesc = useContent(
    "open_house.role_parent.description",
    "RSVP for your family. {{children_line}}",
    { children_line: childrenWelcome ? "Kids are welcome." : "" },
  );
  const instTitle = useContent("open_house.role_instructor.title", "I'm a teacher");
  const instDesc = useContent(
    "open_house.role_instructor.description",
    "Already teaching with Silver Sage or interested in offering a class? RSVP here.",
  );
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <button
        onClick={() => onPick("parent")}
        className="group text-left bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors"
      >
        <Users className="h-6 w-6 text-primary mb-3" />
        <h3 className="font-heading text-xl mb-1">{parentTitle}</h3>
        <p className="text-sm text-muted-foreground">{parentDesc}</p>
      </button>
      <button
        onClick={() => onPick("teacher")}
        className="group text-left bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors"
      >
        <GraduationCap className="h-6 w-6 text-primary mb-3" />
        <h3 className="font-heading text-xl mb-1">{instTitle}</h3>
        <p className="text-sm text-muted-foreground">{instDesc}</p>
      </button>
    </div>
  );
}

function ParentForm({
  event, classes, onBack, onSubmitted,
}: {
  event: EventRow; classes: ClassOption[]; onBack: () => void; onSubmitted: (h: number) => void;
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [adults, setAdults] = useState(1);
  const [kids, setKids] = useState(0);
  const [howHeard, setHowHeard] = useState("");
  const [questions, setQuestions] = useState("");
  const [consent, setConsent] = useState(true);
  const [children, setChildren] = useState<{ child_name: string; age: string }[]>([]);
  const [selectedClassIds, setSelectedClassIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setChildren((prev) => {
      const next = [...prev];
      while (next.length < kids) next.push({ child_name: "", age: "" });
      return next.slice(0, kids);
    });
  }, [kids]);

  const toggleClass = (id: string) => {
    setSelectedClassIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast({ title: "A few fields are missing", description: "We need your name and email to RSVP you.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const classInterests = Array.from(selectedClassIds).map((id) => {
      const c = classes.find((x) => x.id === id)!;
      return { class_id: id, class_title_snapshot: c.title, interest_type: "parent_interest" as const };
    });
    const { data, error } = await supabase.functions.invoke("submit-open-house-registration", {
      body: {
        event_id: event.id,
        attendee_type: "parent",
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        adult_count: adults,
        child_count: kids,
        additional_guest_count: 0,
        how_heard: howHeard.trim() || null,
        questions: questions.trim() || null,
        communication_consent: consent,
        children,
        class_interests: classInterests,
      },
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "We couldn't save your RSVP", description: "Please try again in a moment.", variant: "destructive" });
      return;
    }
    const payload = data as { ok?: boolean; error?: string; total_headcount?: number };
    if (!payload?.ok) {
      toast({ title: payload?.error ?? "Something went wrong", variant: "destructive" });
      return;
    }
    onSubmitted(payload.total_headcount ?? adults + kids);
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      <BackButton onClick={onBack} />
      <Card>
        <CardHeader><CardTitle className="font-heading">Tell us about your family</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="First name" required><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required maxLength={80} /></Field>
            <Field label="Last name" required><Input value={lastName} onChange={(e) => setLastName(e.target.value)} required maxLength={80} /></Field>
            <Field label="Email" required><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={200} /></Field>
            <Field label="Phone (optional)"><Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Adults attending"><CountInput value={adults} onChange={setAdults} min={1} max={10} /></Field>
            <Field label="Children attending"><CountInput value={kids} onChange={setKids} min={0} max={10} /></Field>
          </div>
          {children.length > 0 && (
            <div className="space-y-2">
              <Label>Children's names & ages (optional)</Label>
              {children.map((c, i) => (
                <div key={i} className="grid grid-cols-2 gap-3">
                  <Input placeholder="Name" value={c.child_name} onChange={(e) => setChildren((p) => p.map((x, ix) => ix === i ? { ...x, child_name: e.target.value } : x))} />
                  <Input placeholder="Age" value={c.age} onChange={(e) => setChildren((p) => p.map((x, ix) => ix === i ? { ...x, age: e.target.value } : x))} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {classes.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="font-heading">Classes you'd like to learn more about</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-2">
            {classes.map((c) => (
              <label key={c.id} className="flex items-center gap-2 py-1.5 text-sm cursor-pointer">
                <Checkbox checked={selectedClassIds.has(c.id)} onCheckedChange={() => toggleClass(c.id)} />
                <span>{c.title}</span>
              </label>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="font-heading">A few more details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Field label="How did you hear about us? (optional)"><Input value={howHeard} onChange={(e) => setHowHeard(e.target.value)} maxLength={200} /></Field>
          <Field label="Questions for us? (optional)"><Textarea value={questions} onChange={(e) => setQuestions(e.target.value)} maxLength={2000} rows={3} /></Field>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} />
            <span>It's OK to email me updates about this event.</span>
          </label>
        </CardContent>
      </Card>

      <Button type="submit" size="lg" disabled={submitting} className="w-full">
        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
        Save my RSVP
      </Button>
    </form>
  );
}

function TeacherForm({
  event, classes, onBack, onSubmitted,
}: {
  event: EventRow; classes: ClassOption[]; onBack: () => void; onSubmitted: (h: number) => void;
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState<"currently_teaches" | "interested_in_teaching">("interested_in_teaching");
  const [currentClassIds, setCurrentClassIds] = useState<Set<string>>(new Set());
  const [ideaTitle, setIdeaTitle] = useState("");
  const [ideaAges, setIdeaAges] = useState("");
  const [ideaDesc, setIdeaDesc] = useState("");
  const [questions, setQuestions] = useState("");
  const [consent, setConsent] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      toast({ title: "A few fields are missing", description: "We need your name and email to RSVP you.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const interests: any[] = [];
    if (status === "currently_teaches") {
      for (const id of currentClassIds) {
        const c = classes.find((x) => x.id === id);
        if (c) interests.push({ class_id: id, class_title_snapshot: c.title, interest_type: "currently_teaches" });
      }
    }
    if (ideaTitle.trim()) {
      interests.push({
        class_id: null,
        class_title_snapshot: ideaTitle.trim(),
        interest_type: "interested_in_teaching",
        is_write_in: true,
        write_in_title: ideaTitle.trim(),
        write_in_description: ideaDesc.trim() || null,
        write_in_ages: ideaAges.trim() || null,
      });
    }
    const { data, error } = await supabase.functions.invoke("submit-open-house-registration", {
      body: {
        event_id: event.id,
        attendee_type: "teacher",
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        adult_count: 1,
        child_count: 0,
        additional_guest_count: 0,
        instructor_status: status === "currently_teaches" ? "Currently teaching" : "Interested in teaching",
        questions: questions.trim() || null,
        communication_consent: consent,
        class_interests: interests,
      },
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "We couldn't save your RSVP", description: "Please try again in a moment.", variant: "destructive" });
      return;
    }
    const payload = data as { ok?: boolean; error?: string; total_headcount?: number };
    if (!payload?.ok) {
      toast({ title: payload?.error ?? "Something went wrong", variant: "destructive" });
      return;
    }
    onSubmitted(payload.total_headcount ?? 1);
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      <BackButton onClick={onBack} />
      <Card>
        <CardHeader><CardTitle className="font-heading">Your information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="First name" required><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required maxLength={80} /></Field>
            <Field label="Last name" required><Input value={lastName} onChange={(e) => setLastName(e.target.value)} required maxLength={80} /></Field>
            <Field label="Email" required><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={200} /></Field>
            <Field label="Phone (optional)"><Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} /></Field>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-heading">Are you already teaching with us?</CardTitle></CardHeader>
        <CardContent>
          <RadioGroup value={status} onValueChange={(v) => setStatus(v as any)}>
            <label className="flex items-center gap-2 py-1.5 cursor-pointer">
              <RadioGroupItem value="currently_teaches" id="ct" />
              <span>Yes, I'm a current instructor</span>
            </label>
            <label className="flex items-center gap-2 py-1.5 cursor-pointer">
              <RadioGroupItem value="interested_in_teaching" id="it" />
              <span>Not yet — I'd like to explore teaching</span>
            </label>
          </RadioGroup>
        </CardContent>
      </Card>

      {status === "currently_teaches" && classes.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="font-heading">Which classes do you currently teach?</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-2">
            {classes.map((c) => (
              <label key={c.id} className="flex items-center gap-2 py-1.5 text-sm cursor-pointer">
                <Checkbox
                  checked={currentClassIds.has(c.id)}
                  onCheckedChange={() => setCurrentClassIds((p) => {
                    const n = new Set(p); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n;
                  })}
                />
                <span>{c.title}</span>
              </label>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="font-heading">
            {status === "currently_teaches" ? "Have a new class idea? (optional)" : "Tell us about a class you'd like to teach"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field label="Class title"><Input value={ideaTitle} onChange={(e) => setIdeaTitle(e.target.value)} maxLength={200} placeholder="e.g. Beginner Watercolor" /></Field>
          <Field label="Ages"><Input value={ideaAges} onChange={(e) => setIdeaAges(e.target.value)} maxLength={100} placeholder="e.g. 8–13" /></Field>
          <Field label="What would it cover?"><Textarea value={ideaDesc} onChange={(e) => setIdeaDesc(e.target.value)} maxLength={1500} rows={4} /></Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-heading">Anything else?</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Field label="Questions for us? (optional)"><Textarea value={questions} onChange={(e) => setQuestions(e.target.value)} maxLength={2000} rows={3} /></Field>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} />
            <span>It's OK to email me updates about this event.</span>
          </label>
        </CardContent>
      </Card>

      <Button type="submit" size="lg" disabled={submitting} className="w-full">
        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
        Save my RSVP
      </Button>
    </form>
  );
}

function SubmittedState({ headcount, event, onReset }: { headcount: number; event: EventRow; onReset: () => void }) {
  const title = useContent("open_house.submitted.title", "You're on the list");
  const body = useContent(
    "open_house.submitted.body",
    "Thanks — we've got you down for {{headcount}} attendee{{plural}} at {{event_title}} on {{event_date}}. A confirmation email is on its way with everything you need to know.",
    {
      headcount,
      plural: headcount === 1 ? "" : "s",
      event_title: event.title,
      event_date: formatDate(event.event_date),
    },
  );
  return (
    <Card>
      <CardContent className="py-12 text-center space-y-4">
        <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
        <h1 className="font-heading text-3xl">{title}</h1>
        <p className="text-muted-foreground max-w-md mx-auto">{body}</p>
        <Button variant="outline" onClick={onReset}>RSVP another person</Button>
      </CardContent>
    </Card>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}{required && <span className="text-destructive"> *</span>}</Label>
      {children}
    </div>
  );
}
function CountInput({ value, onChange, min, max }: { value: number; onChange: (n: number) => void; min: number; max: number }) {
  return (
    <div className="flex items-center gap-2">
      <Button type="button" variant="outline" size="icon" onClick={() => onChange(Math.max(min, value - 1))}>−</Button>
      <div className="w-10 text-center font-medium">{value}</div>
      <Button type="button" variant="outline" size="icon" onClick={() => onChange(Math.min(max, value + 1))}>+</Button>
    </div>
  );
}
function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="h-3.5 w-3.5" /> Back
    </button>
  );
}
